import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CreditCard,
  Crown,
  Package,
  Zap,
  BarChart3,
  Receipt,
  Wallet,
  ExternalLink,
  ArrowLeft,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { cn } from "@/lib/utils";
import { billingApi } from "@/services/api";

// Stripe-only billing tabs, hidden for manual (invoice-billed) organisations.
const STRIPE_ONLY_TABS = new Set([
  "plans",
  "addons",
  "credits",
  "payment-methods",
]);

export const BillingLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const currentPath = location.pathname;

  const billingTabs = [
    {
      id: "overview",
      label: t("billing.tabs.overview"),
      icon: CreditCard,
      path: "/settings/billing",
    },
    {
      id: "plans",
      label: t("billing.tabs.plans"),
      icon: Crown,
      path: "/settings/billing/plans",
    },
    {
      id: "addons",
      label: t("billing.tabs.addons"),
      icon: Package,
      path: "/settings/billing/addons",
    },
    {
      id: "credits",
      label: t("billing.tabs.credits"),
      icon: Zap,
      path: "/settings/billing/credits",
    },
    {
      id: "usage",
      label: t("billing.tabs.usage"),
      icon: BarChart3,
      path: "/settings/billing/usage",
    },
    {
      id: "invoices",
      label: t("billing.tabs.invoices"),
      icon: Receipt,
      path: "/settings/billing/invoices",
    },
    {
      id: "payment-methods",
      label: t("billing.tabs.payment"),
      icon: Wallet,
      path: "/settings/billing/payment-methods",
    },
  ];

  // Fetch subscription to check if user has Stripe customer
  const { data: subscription } = useQuery({
    queryKey: ["subscription"],
    queryFn: () => billingApi.getSubscription().then((res) => res.data?.data),
    retry: false,
    staleTime: 30000,
  });

  const hasStripeCustomer = subscription?.stripeCustomerId;
  const isPaidPlan = subscription?.plan && subscription.plan !== "free";
  // Organisations whose plan is negotiated don't use Stripe; hide every
  // self-serve surface (plans, add-ons, credits, payment methods, billing
  // portal). Keyed on the plan as well as the billing mode: `custom` is always
  // sales-managed, and the backend refuses these purchases either way
  // (billing.service.assertSelfServeBillingAllowed), so showing them would only
  // offer the customer actions that cannot succeed.
  const isManualInvoice =
    subscription?.billingMode === "invoice" || subscription?.plan === "custom";
  const visibleTabs = isManualInvoice
    ? billingTabs.filter((tab) => !STRIPE_ONLY_TABS.has(tab.id))
    : billingTabs;

  // Determine active tab
  const getActiveTab = () => {
    if (currentPath === "/settings/billing") return "overview";
    const match = billingTabs.find(
      (tab) =>
        tab.path !== "/settings/billing" && currentPath.startsWith(tab.path),
    );
    return match?.id || "overview";
  };

  const activeTab = getActiveTab();

  // Manual-invoice orgs deep-linking to a Stripe-only tab get bounced to
  // Overview (the tab is hidden from nav, but the route still exists).
  useEffect(() => {
    if (isManualInvoice && STRIPE_ONLY_TABS.has(activeTab)) {
      navigate("/settings/billing", { replace: true });
    }
  }, [isManualInvoice, activeTab, navigate]);

  const handleManageBilling = async () => {
    if (!hasStripeCustomer) {
      navigate("/settings/billing/plans");
      return;
    }

    try {
      const response = await billingApi.createPortalSession();
      window.location.href = response.data.data.url;
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error("Please subscribe to a plan first");
        navigate("/settings/billing/plans");
      } else {
        toast.error("Failed to open billing portal");
      }
    }
  };

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page Header with Back Button */}
      <div className="flex items-center gap-4">
        <Link
          to="/settings"
          className="flex items-center justify-center h-10 w-10 sm:h-8 sm:w-8 rounded-lg border hover:bg-accent transition-colors touch-target"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight truncate">
            {t("billing.billingAndSubscription")}
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base truncate">
            {isManualInvoice
              ? "Your plan is billed by invoice — view your usage and invoices"
              : "Manage your subscription, add-ons, credits, and payment methods"}
          </p>
        </div>
        {!isMobile && isManualInvoice && (
          <span className="inline-flex items-center rounded-md border px-3 py-1.5 text-sm text-muted-foreground">
            <Receipt className="h-4 w-4 mr-2" />
            Billed by invoice
          </span>
        )}
        {!isMobile &&
          !isManualInvoice &&
          (isPaidPlan ? (
            <Button variant="outline" onClick={handleManageBilling}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Billing Portal
            </Button>
          ) : (
            <Button onClick={() => navigate("/settings/billing/plans")}>
              <Crown className="h-4 w-4 mr-2" />
              Upgrade Plan
            </Button>
          ))}
      </div>

      {/* Navigation Tabs */}
      <div className="border-b -mx-4 px-4 sm:mx-0 sm:px-0">
        <nav
          className="-mb-px flex space-x-1 overflow-x-auto scrollbar-none"
          aria-label="Billing tabs"
        >
          {visibleTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;

            return (
              <Link
                key={tab.id}
                to={tab.path}
                className={cn(
                  "flex items-center gap-2 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors",
                  isActive
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30",
                )}
              >
                <Icon className="h-4 w-4" />
                <span className={isMobile ? "" : "hidden sm:inline"}>
                  {tab.label}
                </span>
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Page Content */}
      <div>
        <Outlet />
      </div>
    </div>
  );
};

export default BillingLayout;
